import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Clock, CheckCircle, AlertTriangle, Search, Filter, Download } from 'lucide-react';

interface LoginActivitySummary {
  id: string;
  date: string;
  device: string;
  location: string;
  status: string;
}

interface LoginActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  activities: LoginActivitySummary[];
  isLoading?: boolean;
}

export const LoginActivityModal = ({ 
  isOpen, 
  onClose, 
  activities,
  isLoading = false
}: LoginActivityModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Filter activities based on search and status
  const filteredActivities = activities.filter(activity => {
    const matchesSearch = searchTerm === '' || 
      activity.device.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.date.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
      activity.status.toLowerCase().includes(filterStatus.toLowerCase());
    
    return matchesSearch && matchesFilter;
  });

  // Export activity log
  const exportActivityLog = () => {
    const csvContent = [
      'Date,Device,Location,Status',
      ...filteredActivities.map(activity => 
        `"${activity.date}","${activity.device}","${activity.location}","${activity.status}"`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stewie-trader-login-activity-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'current session':
      case 'current':
        return <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30 text-xs">
          Current
        </Badge>;
      case 'success':
        return <span className="flex items-center text-green-500 dark:text-green-400 text-xs">
          <CheckCircle className="h-3.5 w-3.5 mr-1" />
          Success
        </span>;
      case 'blocked':
      case 'failed':
        return <span className="flex items-center text-red-500 dark:text-red-400 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 mr-1" />
          {status}
        </span>;
      default:
        return <span className="text-xs text-muted-foreground dark:text-slate-400">{status}</span>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] dark:bg-slate-900 dark:border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-slate-100">
            <Clock className="h-5 w-5 text-blue-500" />
            Complete Login Activity
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground dark:text-slate-400" />
              <Input
                placeholder="Search by device, location, or date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-50"
              />
            </div>
            
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 rounded-md border border-input dark:border-slate-600 bg-background dark:bg-slate-700 text-sm dark:text-slate-200"
              >
                <option value="all">All Status</option>
                <option value="current">Current</option>
                <option value="success">Success</option>
                <option value="blocked">Blocked</option>
                <option value="failed">Failed</option>
              </select>
              
              <Button
                variant="outline"
                size="sm"
                onClick={exportActivityLog}
                className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
            </div>
          </div>

          {/* Activity List */}
          <div className="rounded-md border dark:border-slate-700">
            <div className="grid grid-cols-12 bg-muted/50 dark:bg-slate-700/50 py-3 px-4 text-xs font-medium dark:text-slate-300 border-b dark:border-slate-700">
              <div className="col-span-4">Date & Time</div>
              <div className="col-span-3">Device</div>
              <div className="col-span-3">Location</div>
              <div className="col-span-2">Status</div>
            </div>
            
            <ScrollArea className="h-[400px]">
              {isLoading ? (
                <div className="p-8 text-center text-muted-foreground dark:text-slate-400">
                  Loading activity...
                </div>
              ) : filteredActivities.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground dark:text-slate-400">
                  {searchTerm || filterStatus !== 'all' ? 'No activity matches your filters' : 'No login activity found'}
                </div>
              ) : (
                filteredActivities.map((activity, index) => (
                  <div 
                    key={activity.id} 
                    className={`grid grid-cols-12 py-3 px-4 text-sm dark:text-slate-200 hover:bg-muted/30 dark:hover:bg-slate-800/50 ${
                      index < filteredActivities.length - 1 ? 'border-b dark:border-slate-700' : ''
                    }`}
                  >
                    <div className="col-span-4 text-muted-foreground dark:text-slate-400 text-xs flex items-center">
                      <Clock className="h-3.5 w-3.5 mr-1.5 text-muted-foreground dark:text-slate-500" />
                      {activity.date}
                    </div>
                    <div className="col-span-3 truncate" title={activity.device}>
                      {activity.device}
                    </div>
                    <div className="col-span-3 truncate" title={activity.location}>
                      {activity.location}
                    </div>
                    <div className="col-span-2">
                      {getStatusIcon(activity.status)}
                    </div>
                  </div>
                ))
              )}
            </ScrollArea>
          </div>

          {/* Summary Stats */}
          {!isLoading && filteredActivities.length > 0 && (
            <div className="flex justify-between items-center text-xs text-muted-foreground dark:text-slate-400 pt-2 border-t dark:border-slate-700">
              <span>
                Showing {filteredActivities.length} of {activities.length} entries
              </span>
              <span>
                Last login: {activities[0]?.date || 'Unknown'}
              </span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}; 